import { decodeTaggedProps } from '../branches/delta-codec'
import type { TaggedNode } from '../branches/types'
import type { IngestSection } from '../ingest/ingest-api'

/**
 * Pure helpers behind section-scoped ingest surfaces. Log-backed jobs use
 * `sectionsByNode`; legacy jobs fall back to the graph's `ingestSectionId`
 * stamp.
 */

/** The sections that get a tab; merged sections already point at their survivor. */
export function activeSections(sections: readonly IngestSection[] | undefined): IngestSection[] {
  return (sections ?? []).filter((section) => section.status === 'active')
}

/** Follow merged-section pointers to the active section that owns the work. */
export function resolveActiveSectionId(
  sectionId: string,
  sections: readonly IngestSection[] | undefined
): string | undefined {
  const byId = new Map((sections ?? []).map((section) => [section.id, section]))
  const visited = new Set<string>()
  let currentId = sectionId

  while (!visited.has(currentId)) {
    visited.add(currentId)
    const section = byId.get(currentId)
    if (!section) return undefined
    if (section.status === 'active') return section.id
    if (section.status !== 'merged' || !section.mergedInto) return undefined
    currentId = section.mergedInto
  }
  return undefined
}

/** Remap merged memberships to their active survivor, preserving inactive ids. */
export function remapSectionIds(
  sectionIds: readonly string[],
  sections: readonly IngestSection[] | undefined
): string[] {
  const remapped = new Set<string>()
  for (const sectionId of sectionIds) {
    remapped.add(resolveActiveSectionId(sectionId, sections) ?? sectionId)
  }
  return [...remapped]
}

/** A node's stamped section id, decoded from type-tagged compare properties. */
export function nodeSectionId(node: TaggedNode): string | undefined {
  try {
    const props = decodeTaggedProps(node.properties ?? {})
    const value = props.ingestSectionId
    return typeof value === 'string' && value.length > 0 ? value : undefined
  } catch {
    return undefined
  }
}

/** Resolve section membership from the log map, or from the legacy node stamp. */
export function nodeSectionIds(
  node: TaggedNode,
  sectionsByNode: Record<string, string[]> | undefined
): string[] {
  if (sectionsByNode !== undefined) return sectionsByNode[node.id] ?? []
  const legacySectionId = nodeSectionId(node)
  return legacySectionId ? [legacySectionId] : []
}

/** The compare rows scoped to one section; no selection passes everything through. */
export function filterNodesBySection(
  nodes: readonly TaggedNode[],
  sectionId: string | null
): TaggedNode[] {
  if (!sectionId) return [...nodes]
  return nodes.filter((node) => nodeSectionId(node) === sectionId)
}

/** Entity count per section id. Unstamped nodes count toward no section. */
export function sectionEntityCounts(nodes: readonly TaggedNode[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const node of nodes) {
    const sectionId = nodeSectionId(node)
    if (!sectionId) continue
    counts.set(sectionId, (counts.get(sectionId) ?? 0) + 1)
  }
  return counts
}

/**
 * Entity count per section id from log membership. A node shared by multiple
 * sections counts toward each.
 */
export function sectionCountsFromMembership(
  sectionsByNode: Record<string, string[]> | undefined
): Map<string, number> {
  const counts = new Map<string, number>()
  for (const sectionIds of Object.values(sectionsByNode ?? {})) {
    for (const sectionId of sectionIds) {
      counts.set(sectionId, (counts.get(sectionId) ?? 0) + 1)
    }
  }
  return counts
}
